import { PenLine, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import { Dispatch, Income } from "../@types";
import { useUpdateDispatchMutation } from "../dispatchApiSlice";

interface UpdateIncomeProps {
  dispatch: Dispatch;
  refresh: () => void;
}

const UpdateIncome: React.FC<UpdateIncomeProps> = ({ dispatch, refresh }) => {
  const [openModal, setOpenModal] = useState(false);
  const [incomes, setIncomes] = useState<Income[]>(dispatch.incomes);
  const [updatedDispatch, setUpdatedDispatch] = useState<Dispatch>(dispatch);

  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();

  const [updateDispatch, { isLoading, isSuccess, reset }] =
    useUpdateDispatchMutation();

  const handleAddIncome = () => {
    setIncomes([
      ...incomes,
      {
        // id: `expense-${Date.now()}`,
        description: "",
        expectedAmount: 0,
        earnedAmount: 0,
      },
    ]);
  };

  const handleRemoveIncome = (index: number) => {
    setIncomes(incomes.filter((_, i) => i !== index));
  };

  const handleUpdateIncome = (
    index: number,
    field: keyof Income,
    value: string | number,
  ) => {
    const updated = [...incomes];
    updated[index] = { ...updated[index], [field]: value };
    setIncomes(updated);
  };

  // Submit handler
  const handleSubmit = async () => {
    try {
      await updateDispatch({
        id: updatedDispatch.id,
        isCompleted: updatedDispatch.isCompleted,
        incomes: incomes,
        vehicleId: updatedDispatch.vehicleId,
        expenses: updatedDispatch.expenses,
        fuelExpense: updatedDispatch.fuelExpense,
        fromLocation: updatedDispatch.fromLocation,
        toLocation: updatedDispatch.toLocation,
        approximateDistance: updatedDispatch.approximateDistance,
        totalExpectedIncome: updatedDispatch.totalExpectedIncome,
        totalIncomeEarned: updatedDispatch.totalIncomeEarned,
        totalPassengers: updatedDispatch.totalPassengers,
        perPassengerRate: updatedDispatch.perPassengerRate,
        crews: updatedDispatch.crews,
      }).unwrap();

      toast.success({
        title: "Success",
        message: "Dispatch expenses updated successfully",
      });
      setShowSuccess(true);
    } catch (error) {
      const errorMessage =
        getErrorDetails(error).message ||
        "Failed to update dispatch expenses. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  // Close handlers
  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setIncomes(dispatch.incomes || []);
    reset();
  }, [dispatch.incomes, reset]);

  const handleCloseSuccess = useCallback(() => {
    setShowSuccess(false);
    setOpenModal(false);
    reset();
    refresh();
  }, [reset, refresh]);

  // Render success view
  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseSuccess}
          heading="Success"
          message="Incomes have been updated successfully."
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : "Update Incomes";
  return (
    <>
      <div
        onClick={() => setOpenModal(true)}
        className="flex items-end justify-end mt-8 cursor-pointer"
      >
        <div className="flex items-center gap-1 border border-secondary rounded-2xl px-2 py-1 text-[13px] text-secondary hover:bg-secondary/5 transition-colors">
          Edit <PenLine className="h-4 w-4" />
        </div>
      </div>

      <Modal
        isOpen={openModal}
        onClose={handleCloseModal}
        title={modalTitle}
        className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white px-2 pb-4 overflow-hidden"
      >
        {isSuccess ? (
          renderSuccessView()
        ) : (
          <div className="p-6">
            <div className="p-4 space-y-4 border border-gray-200 rounded-[10px]">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <AppInputField
                    label="Total income"
                    type="number"
                    value={updatedDispatch.totalIncome || ""}
                    onChange={(e) =>
                      setUpdatedDispatch({
                        ...updatedDispatch,
                        totalIncome: parseFloat(e.target.value) || 0,
                      })
                    }
                    min={0}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter total income"
                  />

                  <AppInputField
                    label="Total expected income"
                    type="number"
                    value={updatedDispatch.totalExpectedIncome || ""}
                    onChange={(e) =>
                      setUpdatedDispatch({
                        ...updatedDispatch,
                        totalExpectedIncome: parseFloat(e.target.value) || 0,
                      })
                    }
                    min={0}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter total expected income"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <AppInputField
                    label="Total passengers"
                    type="number"
                    value={updatedDispatch.totalPassengers || ""}
                    onChange={(e) =>
                      setUpdatedDispatch({
                        ...updatedDispatch,
                        totalPassengers: parseFloat(e.target.value) || 0,
                      })
                    }
                    min={0}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter total passengers"
                  />

                  <AppInputField
                    label="Rate per passengers"
                    type="number"
                    value={updatedDispatch.perPassengerRate || ""}
                    onChange={(e) =>
                      setUpdatedDispatch({
                        ...updatedDispatch,
                        perPassengerRate: parseFloat(e.target.value) || 0,
                      })
                    }
                    min={0}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter rate per passengers"
                  />
                </div>
                <h3 className="text-[13px] font-semibold">Trip Income</h3>
                <div className="items-center justify-center space-y-4">
                  {incomes.map((income, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-4 w-full"
                    >
                      <AppInputField
                        label={`Description ${index + 1}`}
                        type="text"
                        value={income.description}
                        onChange={(e) =>
                          handleUpdateIncome(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                        className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                        placeholder="Enter trip description"
                      />
                      <AppInputField
                        label={`Amount ${index + 1}`}
                        type="number"
                        value={income.expectedAmount || ""}
                        onChange={(e) =>
                          handleUpdateIncome(
                            index,
                            "expectedAmount",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                        placeholder="Enter expected income"
                      />
                      <AppInputField
                        label={`Amount earned ${index + 1}`}
                        type="number"
                        value={income.earnedAmount || ""}
                        onChange={(e) =>
                          handleUpdateIncome(
                            index,
                            "earnedAmount",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px] w-full"
                        placeholder="Enter income earned"
                      />

                      <div className="w-full md:w-[48px] bg-red-100 flex items-center justify-center h-[48px] rounded-[10px] mt-0 md:mt-8">
                        <button
                          type="button"
                          onClick={() => handleRemoveIncome(index)}
                          className="text-red-500 hover:text-red-600 transition-colors"
                          aria-label="Remove crew member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAddIncome}
                  className="text-secondary hover:text-primary transition-colors text-[13px] font-semibold"
                >
                  + Add Income
                </button>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex justify-start items-center gap-4 pt-4">
              <AppButton
                type="button"
                onClick={handleCloseModal}
                disabled={isLoading}
                className="px-6 py-2 bg-[#F6F6F6] rounded-md text-gray-700 hover:bg-gray-200 transition-colors text-[15px]"
              >
                Cancel
              </AppButton>

              <AppButton
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Updating..." : "Update"}
              </AppButton>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UpdateIncome;
